import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import type { UserRole } from '@seenaly/shared';
import { usePrefs } from '@/app/prefs';
import { screenTitle } from '@/app/screenTitle';
import { Sidebar, type SidebarBadges } from './Sidebar';
import { Topbar } from './Topbar';
import { OfflineBanner } from './OfflineBanner';

interface AppShellProps {
  role: UserRole;
  visibleNavKeys: readonly string[];
  user?: { name: string; roleLabel: string; initials: string };
  badges?: SidebarBadges;
  onLogout?: () => void;
}

/**
 * Sidebar + topbar + scrollable content.
 *
 * Below 900px the sidebar becomes an overlay drawer (SKILL.md). It closes on
 * navigation and on Escape — otherwise tapping a menu item on a phone leaves
 * the drawer covering the screen the user just asked for.
 */
export function AppShell({ role, visibleNavKeys, user, badges, onLogout }: AppShellProps) {
  const { t } = usePrefs();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  const closeNav = useCallback(() => setNavOpen(false), []);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setNavOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  return (
    <div className="sn-shell">
      {navOpen && (
        <button type="button" className="sn-navscrim" onClick={closeNav} aria-label={t.btn.close} />
      )}

      <Sidebar
        role={role}
        visibleNavKeys={visibleNavKeys}
        open={navOpen}
        badges={badges}
        onNavigate={closeNav}
      />

      <div className="sn-main">
        <OfflineBanner />
        <Topbar
          title={screenTitle(location.pathname, t)}
          user={user}
          onToggleNav={() => setNavOpen((open) => !open)}
          onLogout={onLogout}
        />
        <main className="sn-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
